import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Loader2, Save, Sparkles, Globe, Instagram, Facebook, FolderOpen, Briefcase } from 'lucide-react';
import { toast } from 'sonner';
import { serviceDeliveryAPI } from '../lib/api';

const FIELDS = [
  { key: 'resumen', label: 'Resumen del negocio', type: 'textarea', hint: '1-2 líneas: a qué se dedica' },
  { key: 'productos_estrella', label: 'Productos o servicios estrella a destacar', type: 'textarea' },
  { key: 'tono', label: 'Tono de comunicación de la marca', type: 'textarea' },
  { key: 'lineas_rojas', label: 'Cosas que NO se pueden decir o publicar (líneas rojas)', type: 'textarea', hint: 'Temas, palabras o mensajes prohibidos' },
  { key: 'aprueba_contenidos', label: 'Persona que aprueba los contenidos', type: 'text' },
  { key: 'contacto_crisis', label: 'Persona de contacto en caso de crisis', type: 'text' },
];

const LINKS = [
  { key: 'web', label: 'Web', icon: Globe, placeholder: 'https://...' },
  { key: 'instagram', label: 'Instagram', icon: Instagram, placeholder: '@usuario o enlace' },
  { key: 'facebook', label: 'Facebook', icon: Facebook, placeholder: 'Enlace a la página' },
  { key: 'drive', label: 'Carpeta de Google Drive', icon: FolderOpen, placeholder: 'Enlace a la carpeta' },
];

export default function BusinessInfoSection({ clientId }) {
  const [form, setForm] = useState(null);
  const [autoFields, setAutoFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const initialAutoRef = useRef(new Set());
  const editedRef = useRef(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await serviceDeliveryAPI.getBusinessInfo(clientId);
      const auto = Array.isArray(res?.auto_fields) ? res.auto_fields : [];
      setForm(res?.data || {});
      setAutoFields(auto);
      initialAutoRef.current = new Set(auto);
      editedRef.current = new Set();
    } catch (e) {
      toast.error('Error cargando la información del negocio');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { if (clientId) load(); }, [clientId, load]);

  const setVal = (key, v) => {
    setForm((prev) => ({ ...prev, [key]: v }));
    editedRef.current.add(key);
    setAutoFields((prev) => prev.filter((f) => f !== key));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Only persist fields the user edited OR that were already manual overrides on load.
      // Un-edited, briefing-derived fields are sent empty so the backend keeps deriving them.
      const payload = {};
      Object.keys(form).forEach((f) => {
        const isPersistable = editedRef.current.has(f) || !initialAutoRef.current.has(f);
        payload[f] = isPersistable ? (form[f] || '') : '';
      });
      await serviceDeliveryAPI.saveBusinessInfo(clientId, payload);
      toast.success('Información del negocio guardada');
      await load();
    } catch (e) {
      toast.error(e?.message || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !form) {
    return <div className="py-16 text-center text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;
  }

  const AutoBadge = ({ field }) => autoFields.includes(field) ? (
    <Badge variant="outline" className="ml-2 bg-violet-50 text-violet-700 border-violet-200 text-[10px]" data-testid={`business-auto-${field}`}>
      <Sparkles className="w-2.5 h-2.5 mr-1" /> Del briefing
    </Badge>
  ) : null;

  return (
    <Card data-testid="business-info-section">
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <CardTitle className="flex items-center gap-2"><Briefcase className="w-5 h-5 text-primary" /> Información del Negocio</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">Chuleta rápida para el equipo. Se autocompleta desde el briefing del cliente; puedes editarla o completarla a mano.</p>
          </div>
          <Button onClick={handleSave} disabled={saving} data-testid="business-info-save">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Guardar
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {FIELDS.map((f) => (
          <div key={f.key}>
            <Label className="flex items-center">{f.label}<AutoBadge field={f.key} /></Label>
            {f.hint && <p className="text-xs text-muted-foreground mb-1">{f.hint}</p>}
            {f.type === 'textarea' ? (
              <Textarea value={form[f.key] || ''} onChange={(e) => setVal(f.key, e.target.value)} rows={3} className="resize-none" data-testid={`business-${f.key}`} />
            ) : (
              <Input value={form[f.key] || ''} onChange={(e) => setVal(f.key, e.target.value)} data-testid={`business-${f.key}`} />
            )}
          </div>
        ))}

        <div>
          <Label className="mb-2 block">Enlaces rápidos</Label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {LINKS.map((l) => {
              const Icon = l.icon;
              return (
                <div key={l.key} className="flex items-center gap-2">
                  <span className="shrink-0 w-9 h-9 rounded-md bg-muted flex items-center justify-center" title={l.label}><Icon className="w-4 h-4 text-muted-foreground" /></span>
                  <div className="flex-1 min-w-0">
                    <span className="text-xs text-muted-foreground flex items-center">{l.label}<AutoBadge field={l.key} /></span>
                    <Input value={form[l.key] || ''} onChange={(e) => setVal(l.key, e.target.value)} placeholder={l.placeholder} className="h-8 text-sm" data-testid={`business-${l.key}`} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div>
          <Label className="flex items-center">Notas internas del equipo<AutoBadge field="notas_internas" /></Label>
          <p className="text-xs text-muted-foreground mb-1">Campo libre, solo visible para el equipo.</p>
          <Textarea value={form.notas_internas || ''} onChange={(e) => setVal('notas_internas', e.target.value)} rows={4} className="resize-none" data-testid="business-notas_internas" />
        </div>
      </CardContent>
    </Card>
  );
}
